/**
 * Stage Plot Canvas (SVG/DOM)
 *
 * Fixed stage backdrop sized in STAGE UNITS * STAGE_SCALE, rendered inside a
 * scrollable viewport. Elements are absolutely-positioned divs each
 * containing a recoloured StageIcon SVG.
 *
 * Positions (el.x/el.y) and sizes (el.width/el.height) are in METERS; the
 * canvas multiplies by STAGE_SCALE for pixels. This keeps everything to scale.
 */
import { useRef, useEffect, useCallback, useState, type DragEvent } from 'react';
import type { StagePlot, StageElement } from '../../shared/types/stageplot';
import { STAGE_SCALE } from '../../shared/types/stageplot';
import { StageIcon, STAGE_ICON_BANK, STAGE_IMAGE_MAP, STAGE_SVG_GRAY } from './StageIcon';
import { drumKitToSvg, type DrumKitConfig } from './drumKitConfig';
import { riserToSvg, riserTotalSpec, type RiserConfig } from './riserConfig';
import { spdToSvg, type SpdConfig } from './spdConfig';
import { DrumKitGraphic } from './DrumKitGraphic';
import { RiserGraphic } from './RiserGraphic';

export interface StagePlotCanvasProps {
  plot: StagePlot;
  selectedIds: string[];
  zoom: number;
  snapEnabled: boolean;
  onMoveElement: (id: string, x: number, y: number) => void;
  onMoveElements?: (deltas: { id: string; x: number; y: number }[]) => void;
  onResizeElement: (id: string, width: number, height: number) => void;
  onRotateElement: (id: string, rotation: number) => void;
  onUpdateElement: (id: string, patch: Partial<StageElement>) => void;
  onAddElement: (el: StageElement) => void;
  onSelectElement: (id: string | null) => void;
  onSelectElements: (ids: string[]) => void;
  onExport?: (cb: () => void) => void;
}

type DragMode = 'move' | 'resize' | 'rotate' | 'marquee' | null;

// Defensive normalisation: legacy / partial plots may omit fields.
import { DEFAULT_STAGE_DIMENSIONS, STAGEPLOT_SCHEMA_VERSION, type StagePlot as StagePlotType } from '../../shared/types/stageplot';

export function normalizePlot(plot: StagePlotType): StagePlotType {
  const safe =
    plot && typeof plot === 'object'
      ? plot
      : ({ name: 'Stage Plot', elements: [] } as unknown as StagePlotType);
  return {
    ...safe,
    stageDimensions:
      safe.stageDimensions && typeof safe.stageDimensions.width === 'number'
        ? safe.stageDimensions
        : { ...DEFAULT_STAGE_DIMENSIONS },
    elements: Array.isArray(safe.elements) ? safe.elements : [],
    metadata:
      safe.metadata && typeof safe.metadata === 'object'
        ? safe.metadata
        : {
            bandName: '',
            venue: '',
            date: '',
            notes: '',
            created: 0,
            lastModified: 0,
            schemaVersion: STAGEPLOT_SCHEMA_VERSION,
          },
  };
}

interface DragState {
  mode: DragMode;
  id: string; // primary element for resize/rotate
  ids: string[]; // all moving elements
  startX: number;
  startY: number;
  origBy: Record<string, { x: number; y: number; w: number; h: number }>;
  snap: boolean;
  marqueeStart?: { x: number; y: number };
  marqueeEnd?: { x: number; y: number };
}

const SNAP_GRID = 0.5; // meters
const GRID_MINOR = 0.5; // 50 cm minor grid (25 px)
const GRID_MAJOR = 1; // 1 m major grid (50 px)

function round2(v: number) {
  return Math.round(v * 100) / 100;
}

function snap(v: number, grid: number) {
  return Math.round(v / grid) * grid;
}

interface Guide {
  orientation: 'v' | 'h';
  pos: number;
  strong: boolean;
}

export default function StagePlotCanvas({
  plot: plotProp,
  selectedIds,
  zoom,
  snapEnabled,
  onMoveElement,
  onMoveElements,
  onResizeElement,
  onRotateElement,
  onUpdateElement,
  onAddElement,
  onSelectElement,
  onSelectElements,
  onExport,
}: StagePlotCanvasProps) {
  const plot = normalizePlot(plotProp);
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const stageW = plot.stageDimensions.width * STAGE_SCALE;
  const stageH = plot.stageDimensions.height * STAGE_SCALE;
  const selSet = new Set(selectedIds);

  // ── Export hook (PNG via canvas snapshot of a generated SVG) ──
  useEffect(() => {
    if (!onExport) return;
    const cb = () => exportPng(plot, stageW, stageH);
    onExport(cb);
  }, [onExport, plot, stageW, stageH]);

  // Compute alignment guides relative to the moving element's edges/centers.
  const computeGuides = useCallback(
    (moving: StageElement): Guide[] => {
      const out: Guide[] = [];
      const stageCx = plot.stageDimensions.width / 2;
      const stageCy = plot.stageDimensions.height / 2;
      const mCx = moving.x + moving.width / 2;
      const mCy = moving.y + moving.height / 2;
      const mLeft = moving.x;
      const mRight = moving.x + moving.width;
      const mTop = moving.y;
      const mBottom = moving.y + moving.height;

      const consider = (pos: number, axis: 'v' | 'h', strong: boolean) => {
        const ref = axis === 'v' ? mCx : mCy;
        if (Math.abs(ref - pos) < 0.05) out.push({ orientation: axis, pos, strong });
      };

      consider(stageCx, 'v', true);
      consider(stageCy, 'h', true);

      for (const el of plot.elements) {
        if (selSet.has(el.id)) continue;
        const cx = el.x + el.width / 2;
        const cy = el.y + el.height / 2;
        if (Math.abs(mCx - cx) < 0.05) out.push({ orientation: 'v', pos: cx, strong: true });
        if (Math.abs(mCy - cy) < 0.05) out.push({ orientation: 'h', pos: cy, strong: true });
        if (Math.abs(mLeft - el.x) < 0.05) out.push({ orientation: 'v', pos: el.x, strong: false });
        if (Math.abs(mRight - (el.x + el.width)) < 0.05)
          out.push({ orientation: 'v', pos: el.x + el.width, strong: false });
        if (Math.abs(mTop - el.y) < 0.05) out.push({ orientation: 'h', pos: el.y, strong: false });
        if (Math.abs(mBottom - (el.y + el.height)) < 0.05)
          out.push({ orientation: 'h', pos: el.y + el.height, strong: false });
      }
      return out;
    },
    [plot.elements, plot.stageDimensions, selSet],
  );

  // ── Coordinate helper: client px -> stage units (meters) ──
  const toStageUnits = useCallback(
    (clientX: number, clientY: number) => {
      const stage = stageRef.current;
      if (!stage) return { x: 0, y: 0 };
      const rect = stage.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / STAGE_SCALE / zoom,
        y: (clientY - rect.top) / STAGE_SCALE / zoom,
      };
    },
    [zoom],
  );

  // ── Window-level pointer listeners (robust drag) ──
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dxUnits = (e.clientX - d.startX) / STAGE_SCALE / zoom;
      const dyUnits = (e.clientY - d.startY) / STAGE_SCALE / zoom;

      if (d.mode === 'marquee') {
        const end = toStageUnits(e.clientX, e.clientY);
        const s = d.marqueeStart!;
        setMarquee({
          x: Math.min(s.x, end.x) * STAGE_SCALE,
          y: Math.min(s.y, end.y) * STAGE_SCALE,
          w: Math.abs(end.x - s.x) * STAGE_SCALE,
          h: Math.abs(end.y - s.y) * STAGE_SCALE,
        });
        return;
      }

      if (d.mode === 'move') {
        const ids = d.ids.length ? d.ids : [d.id];
        const last = d.origBy[d.id];
        let nx = last.x + dxUnits;
        let ny = last.y + dyUnits;
        const snapping = d.snap && !e.shiftKey; // Shift disables snap during drag
        if (snapping) {
          nx = snap(nx, SNAP_GRID);
          ny = snap(ny, SNAP_GRID);
        }
        const ddx = nx - last.x;
        const ddy = ny - last.y;
        if (onMoveElements) {
          onMoveElements(
            ids.map((id) => ({
              id,
              x: round2(d.origBy[id].x + ddx),
              y: round2(d.origBy[id].y + ddy),
            })),
          );
        } else {
          for (const id of ids) {
            onMoveElement(id, round2(d.origBy[id].x + ddx), round2(d.origBy[id].y + ddy));
          }
        }
        const moving: StageElement = {
          id: d.id,
          iconId: 'x',
          category: 'stage',
          label: '',
          x: nx,
          y: ny,
          width: last.w,
          height: last.h,
          rotation: 0,
          color: '#000',
        };
        setGuides(snapping ? computeGuides(moving) : []);
      } else if (d.mode === 'resize') {
        const orig = d.origBy[d.id];
        let nw = Math.max(0.2, orig.w + dxUnits);
        let nh = Math.max(0.2, orig.h + dyUnits);
        const snapping = d.snap && !e.shiftKey;
        if (snapping) {
          nw = Math.round(nw / GRID_MINOR) * GRID_MINOR;
          nh = Math.round(nh / GRID_MINOR) * GRID_MINOR;
        }
        // Scale all selected elements relative to the primary's factor.
        const fx = nw / orig.w;
        const fy = nh / orig.h;
        for (const id of d.ids) {
          if (id === d.id) {
            onResizeElement(id, round2(nw), round2(nh));
          } else {
            const o = d.origBy[id];
            onResizeElement(id, round2(o.w * fx), round2(o.h * fy));
          }
        }
      } else if (d.mode === 'rotate') {
        const stage = stageRef.current;
        if (!stage) return;
        const o = d.origBy[d.id];
        const rect = stage.getBoundingClientRect();
        const ctrPxX = rect.left + (o.x + o.w / 2) * STAGE_SCALE * zoom;
        const ctrPxY = rect.top + (o.y + o.h / 2) * STAGE_SCALE * zoom;
        const ang =
          (Math.atan2(e.clientY - ctrPxY, e.clientX - ctrPxX) * 180) / Math.PI + 90;
        const snapping = d.snap && !e.shiftKey;
        const step = snapping ? 15 : 5;
        for (const id of d.ids) onRotateElement(id, Math.round(ang / step) * step);
      }
    };
    const onUp = () => {
      const d = drag.current;
      if (d && d.mode === 'marquee' && d.marqueeStart) {
        const s = d.marqueeStart;
        const end = d.marqueeEnd ?? s;
        const x1 = Math.min(s.x, end.x);
        const y1 = Math.min(s.y, end.y);
        const x2 = Math.max(s.x, end.x);
        const y2 = Math.max(s.y, end.y);
        if (Math.abs(x2 - x1) > 0.1 || Math.abs(y2 - y1) > 0.1) {
          const hit = plot.elements
            .filter((el) => el.x < x2 && el.x + el.width > x1 && el.y < y2 && el.y + el.height > y1)
            .map((el) => el.id);
          onSelectElements(hit);
        }
      }
      drag.current = null;
      setGuides([]);
      setMarquee(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [zoom, plot.elements, plot.stageDimensions, onMoveElement, onMoveElements, onResizeElement, onRotateElement, onSelectElements, computeGuides]);

  const beginDrag = useCallback(
    (e: React.PointerEvent, el: StageElement, mode: DragMode) => {
      e.stopPropagation();
      // Shift+click toggles membership in the multi-selection (add/remove one element).
      if (e.shiftKey) {
        const next = selSet.has(el.id)
          ? selectedIds.filter((id) => id !== el.id)
          : [...selectedIds, el.id];
        onSelectElements(next);
        // Build drag group from the new selection so a drag afterwards moves the whole set.
        const ids = next.length ? next : [el.id];
        const origBy: DragState['origBy'] = {};
        for (const id of ids) {
          const o = plot.elements.find((x) => x.id === id);
          if (o) origBy[id] = { x: o.x, y: o.y, w: o.width, h: o.height };
        }
        drag.current = {
          mode,
          id: el.id,
          ids,
          startX: e.clientX,
          startY: e.clientY,
          origBy,
          snap: snapEnabled && !e.shiftKey,
        };
        return;
      }
      // Clicking an already-selected element keeps the current group (for group drag);
      // clicking any other element selects just it (replacing the selection).
      const alreadySelected = selSet.has(el.id);
      if (!alreadySelected) onSelectElement(el.id);
      const ids = alreadySelected && selectedIds.length > 0 ? selectedIds : [el.id];
      const origBy: DragState['origBy'] = {};
      for (const id of ids) {
        const o = plot.elements.find((x) => x.id === id);
        if (o) origBy[id] = { x: o.x, y: o.y, w: o.width, h: o.height };
      }
      drag.current = {
        mode,
        id: el.id,
        ids,
        startX: e.clientX,
        startY: e.clientY,
        origBy,
        snap: snapEnabled && !e.shiftKey,
      };
    },
    [selSet, selectedIds, plot.elements, onSelectElement, onSelectElements, snapEnabled],
  );

  // ── Drop from palette ──
  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData('application/stageplot-item');
      if (!raw) return;
      try {
        const item = JSON.parse(raw);
        const sp = toStageUnits(e.clientX, e.clientY);
        const x = Math.max(0, sp.x - item.defaultWidth / 2);
        const y = Math.max(0, sp.y - item.defaultHeight / 2);
        const el: StageElement = {
          id: `el-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`,
          iconId: item.iconId,
          category: item.category,
          label: item.label,
          x: round2(snap(x, SNAP_GRID)),
          y: round2(snap(y, SNAP_GRID)),
          width: item.defaultWidth,
          height: item.defaultHeight,
          rotation: 0,
          color: item.color,
        };
        onAddElement(el);
      } catch {
        /* ignore malformed payload */
      }
    },
    [toStageUnits, onAddElement],
  );

  const onStagePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.target !== e.currentTarget) return;
      // Shift+click on empty stage should NOT clear the current selection (lets you keep a set).
      if (e.shiftKey) {
        drag.current = { mode: null, id: '', ids: [], startX: e.clientX, startY: e.clientY, origBy: {}, snap: false };
        return;
      }
      // Start a marquee selection.
      const sp = toStageUnits(e.clientX, e.clientY);
      drag.current = {
        mode: 'marquee',
        id: '',
        ids: [],
        startX: e.clientX,
        startY: e.clientY,
        origBy: {},
        snap: false,
        marqueeStart: sp,
        marqueeEnd: sp,
      };
      // Update marqueeEnd live
      const move = (ev: PointerEvent) => {
        const d = drag.current;
        if (d && d.mode === 'marquee') d.marqueeEnd = toStageUnits(ev.clientX, ev.clientY);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      onSelectElement(null);
    },
    [toStageUnits, onSelectElement],
  );

  // Build grid background: minor 50cm (25px) + major 1m (50px)
  const minorPx = GRID_MINOR * STAGE_SCALE;
  const majorPx = GRID_MAJOR * STAGE_SCALE;

  return (
    <div
      className="stageplot-canvas-viewport"
      style={{ flex: 1, overflow: 'auto', position: 'relative', padding: 24, background: 'var(--bg)' }}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <div
        ref={stageRef}
        className="stageplot-stage"
        style={{
          position: 'relative',
          width: stageW,
          height: stageH,
          background: '#fff',
          border: '2px solid #ccc',
          backgroundImage: `
            linear-gradient(to right, #f0f0f0 1px, transparent 1px),
            linear-gradient(to bottom, #f0f0f0 1px, transparent 1px),
            linear-gradient(to right, #d8d8d8 1px, transparent 1px),
            linear-gradient(to bottom, #d8d8d8 1px, transparent 1px)`,
          backgroundSize: `
            ${minorPx}px ${minorPx}px,
            ${minorPx}px ${minorPx}px,
            ${majorPx}px ${majorPx}px,
            ${majorPx}px ${majorPx}px`,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        onPointerDown={onStagePointerDown}
      >
        {plot.elements.map((el, i) => {
          const isSel = selSet.has(el.id);
          const isKit = el.iconId === 'drumkit';
          const isRiser = el.iconId === 'risers';
          const isPrimary = isSel && selectedIds[selectedIds.length - 1] === el.id;
          return (
            <div
              key={el.id}
              className={`stageplot-element ${isSel ? 'selected' : ''}`}
              style={{
                position: 'absolute',
                left: el.x * STAGE_SCALE,
                top: el.y * STAGE_SCALE,
                width: el.width * STAGE_SCALE,
                height: el.height * STAGE_SCALE,
                transform: `rotate(${el.rotation}deg)`,
                cursor: 'move',
                color: el.color,
                zIndex: i + 1,
              }}
              onPointerDown={(e) => beginDrag(e, el, 'move')}
            >
              <div
                className="stageplot-element-graphic"
                aria-label={el.label || el.iconId}
                style={el.flipX ? { transform: 'scaleX(-1)' } : undefined}
              >
                {isKit ? (
                  <DrumKitGraphic
                    config={(el.config as unknown) as DrumKitConfig}
                    color={el.color}
                    size={Math.min(el.width, el.height) * STAGE_SCALE}
                  />
                ) : isRiser ? (
                  <RiserGraphic
                    config={(el.config as unknown) as RiserConfig}
                    color={el.color}
                    boxW={el.width * STAGE_SCALE}
                    boxH={el.height * STAGE_SCALE}
                  />
                ) : (
                  <StageIcon iconId={el.iconId} color={el.color} size={Math.min(el.width, el.height) * STAGE_SCALE} />
                )}
              </div>
              {isRiser ? (() => {
                const riserCfg = (el.config as unknown) as RiserConfig;
                const spec = riserTotalSpec(riserCfg);
                const display = el.label && el.label.trim() && el.label !== 'Risers' ? el.label : spec;
                const avail = el.width * STAGE_SCALE;
                const fitFont = Math.floor(avail / Math.max(1, display.length) / 0.6);
                const fontSize = Math.max(6, Math.min(11, fitFont));
                return (
                  <input
                    className="stageplot-element-name-input"
                    type="text"
                    value={display}
                    aria-label="Riser name"
                    onPointerDown={(e) => e.stopPropagation()}
                    onChange={(e) => onUpdateElement(el.id, { label: e.target.value })}
                    style={{ fontSize: `${fontSize}px` }}
                  />
                );
              })() : (
                el.label && el.showLabel !== false && (
                  <div
                    className="stageplot-element-label"
                    style={el.rotation ? { transform: `rotate(${-el.rotation}deg)` } : undefined}
                  >
                    {el.label}
                  </div>
                )
              )}
              {isPrimary && (
                <>
                  <div
                    className="stageplot-handle resize"
                    title="Resize"
                    onPointerDown={(e) => beginDrag(e, el, 'resize')}
                  />
                  <div
                    className="stageplot-handle rotate"
                    title="Rotate"
                    onPointerDown={(e) => beginDrag(e, el, 'rotate')}
                  />
                </>
              )}
            </div>
          );
        })}

        {/* Marquee selection rectangle */}
        {marquee && (
          <div
            style={{
              position: 'absolute',
              left: marquee.x,
              top: marquee.y,
              width: marquee.w,
              height: marquee.h,
              border: '1px solid var(--accent)',
              background: 'rgba(233,69,96,0.12)',
              pointerEvents: 'none',
              zIndex: 6,
            }}
          />
        )}

        {/* Alignment guides */}
        {guides.map((g, i) =>
          g.orientation === 'v' ? (
            <div
              key={`v${i}`}
              className="stageplot-guide"
              style={{
                position: 'absolute',
                left: g.pos * STAGE_SCALE,
                top: 0,
                width: 1,
                height: stageH,
                background: g.strong ? '#E74C3C' : '#4A90D9',
                opacity: g.strong ? 0.9 : 0.5,
                pointerEvents: 'none',
              }}
            />
          ) : (
            <div
              key={`h${i}`}
              className="stageplot-guide"
              style={{
                position: 'absolute',
                top: g.pos * STAGE_SCALE,
                left: 0,
                height: 1,
                width: stageW,
                background: g.strong ? '#E74C3C' : '#4A90D9',
                opacity: g.strong ? 0.9 : 0.5,
                pointerEvents: 'none',
              }}
            />
          ),
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────
// Canonical SVG/PDF + PNG export
// ──────────────────────────────────────────
/** Render the canonical editor model as SVG for PDF and image consumers. */
export function stagePlotToSvg(plot: StagePlot, w = plot.stageDimensions.width * STAGE_SCALE, h = plot.stageDimensions.height * STAGE_SCALE): string {
  const safePlot = normalizePlot(plot);
  const ws = w ?? safePlot.stageDimensions.width * STAGE_SCALE;
  const hs = h ?? safePlot.stageDimensions.height * STAGE_SCALE;
  const svgns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgns, 'svg');
  svg.setAttribute('xmlns', svgns);
  svg.setAttribute('width', String(ws));
  svg.setAttribute('height', String(hs));
  svg.setAttribute('viewBox', `0 0 ${ws} ${hs}`);

  const bg = document.createElementNS(svgns, 'rect');
  bg.setAttribute('x', '0');
  bg.setAttribute('y', '0');
  bg.setAttribute('width', String(ws));
  bg.setAttribute('height', String(hs));
  bg.setAttribute('fill', '#ffffff');
  svg.appendChild(bg);

  // Title block
  const titleGroup = document.createElementNS(svgns, 'g');
  const title = document.createElementNS(svgns, 'text');
  title.setAttribute('x', '12');
  title.setAttribute('y', '22');
  title.setAttribute('font-size', '14');
  title.setAttribute('font-weight', 'bold');
  title.setAttribute('fill', '#222222');
  title.textContent = plot.name || 'Stage Plot';
  titleGroup.appendChild(title);
  if (plot.metadata.bandName) {
    const band = document.createElementNS(svgns, 'text');
    band.setAttribute('x', '12');
    band.setAttribute('y', '40');
    band.setAttribute('font-size', '11');
    band.setAttribute('fill', '#555555');
    band.textContent = plot.metadata.bandName;
    titleGroup.appendChild(band);
  }
  // The third title line shows the VENUE only. The stored `metadata.date` is
  // intentionally NOT painted: it used to appear alone (the venue is empty on
  // most plots) as a stray date on the exported image. The field stays on the
  // type for data compatibility — it is simply no longer rendered.
  if (plot.metadata.venue) {
    const meta = document.createElementNS(svgns, 'text');
    meta.setAttribute('x', '12');
    meta.setAttribute('y', '56');
    meta.setAttribute('font-size', '10');
    meta.setAttribute('fill', '#777777');
    meta.textContent = plot.metadata.venue;
    titleGroup.appendChild(meta);
  }
  svg.appendChild(titleGroup);

  // Stage container offset (leave space for the title block, whose height
  // depends on whether the band line and/or the venue line are emitted).
  const marginTop =
    plot.metadata.bandName && plot.metadata.venue ? 68 : plot.metadata.bandName || plot.metadata.venue ? 52 : 36;
  const margin = 16;
  const availW = w - margin * 2;
  const availH = h - marginTop - margin;
  const scaleX = availW / w;
  const scaleY = availH / h;
  const stageScale = Math.min(scaleX, scaleY, 1);
  const stageX = margin + (availW - w * stageScale) / 2;
  const stageY = marginTop + (availH - h * stageScale) / 2;

  const stageRect = document.createElementNS(svgns, 'rect');
  stageRect.setAttribute('x', String(stageX));
  stageRect.setAttribute('y', String(stageY));
  stageRect.setAttribute('width', String(w * stageScale));
  stageRect.setAttribute('height', String(h * stageScale));
  stageRect.setAttribute('fill', '#ffffff');
  stageRect.setAttribute('stroke', '#dddddd');
  stageRect.setAttribute('stroke-width', '1');
  svg.appendChild(stageRect);

  const ordered = [...safePlot.elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  for (const el of ordered) {
    const g = document.createElementNS(svgns, 'g');
    g.setAttribute(
      'transform',
      `translate(${stageX + el.x * STAGE_SCALE * stageScale}, ${stageY + el.y * STAGE_SCALE * stageScale}) rotate(${el.rotation} ${el.width * STAGE_SCALE * stageScale / 2} ${el.height * STAGE_SCALE * stageScale / 2}) scale(${stageScale})`,
    );
    const inner = document.createElementNS(svgns, 'g');
    let markup = '';
    if (el.iconId === 'drumkit') {
      markup = drumKitToSvg((el.config as unknown) as DrumKitConfig, el.color);
    } else if (el.iconId === 'risers') {
      markup = riserToSvg((el.config as unknown) as RiserConfig, el.color);
    } else if (el.iconId === 'spd') {
      markup = spdToSvg((el.config as unknown) as SpdConfig, el.color);
    } else {
      const pngAsset = STAGE_IMAGE_MAP[el.iconId];
      if (pngAsset) {
        // Real StagePlotPro photo-render. Emit the PNG as an <image> so the
        // exported SVG / PDF uses the new photo assets (matching the
        // interactive editor) instead of the old vector glyph. The <image>
        // fills the 64×64 viewBox and the outer svg's
        // preserveAspectRatio="xMidYMid meet" mirrors the editor's
        // objectFit:'contain' (square image centred in the element box).
        // inlineStagePlotImages() inlines these hrefs to base64 before the
        // PDF rasterization step so they survive SVG secure-static-mode.
        markup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64" preserveAspectRatio="xMidYMid meet"><image href="${pngAsset}" x="0" y="0" width="64" height="64" preserveAspectRatio="xMidYMid meet"/></svg>`;
      } else {
        const bank = STAGE_ICON_BANK[el.iconId];
        if (bank) {
          // New icon bank format: raw SVG string with fill="currentColor".
          // Strip the <svg> root, substitute the colour, and wrap in a sized
          // <svg> with the canonical 64×64 viewBox so it renders at the
          // element's display size inside the scaled <g>. These icons have no
          // StagePlotPro PNG, so they render in the neutral STAGE_SVG_GRAY
          // (ignoring el.color) to stay monochrome like the PNG assets.
          const innerMarkup = bank
            .replace(/^\s*<svg[^>]*>/i, '')
            .replace(/<\/svg>\s*$/i, '')
            .replace(/currentColor/g, STAGE_SVG_GRAY);
          const size = Math.min(el.width, el.height) * STAGE_SCALE;
          markup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}">${innerMarkup}</svg>`;
        }
      }
    }
    // The composite svg strings (riser/drumkit/spd) carry their viewBox in
    // CANONICAL units (cm / 100-space). The <g> is already scaled by
    // stageScale, so we must set width/height to the ELEMENT's real size
    // (px) — otherwise the graphic renders at viewBox size × stageScale
    // (e.g. the riser 2× too big and offset from its element box).
    // preserveAspectRatio mirrors each editor component: risers stretch to
    // fill the box (RiserGraphic uses none); drums/spd keep their ratio.
    const realW = el.width * STAGE_SCALE;
    const realH = el.height * STAGE_SCALE;
    markup = markup.replace(/width="[^"]*"/, `width="${realW}"`).replace(/height="[^"]*"/, `height="${realH}"`);
    if (el.iconId === 'risers') {
      markup = markup.replace(/<svg[^>]*>/i, (m) => (m.includes('preserveAspectRatio') ? m : `${m.replace(/\/>$/, '')} preserveAspectRatio="none">`));
    }
    inner.innerHTML = markup;
    if (el.flipX) {
      const ew = el.width * STAGE_SCALE;
      inner.setAttribute('transform', `translate(${ew} 0) scale(-1 1)`);
    }
    while (inner.firstChild) g.appendChild(inner.firstChild);
    svg.appendChild(g);
    // Label under the element. Risers always show their size spec (the user
    // wants the dimensions of platforms visible even when instrument labels
    // are hidden); other elements respect the showLabel toggle.
    const isRiserEl = el.iconId === 'risers';
    const riserText = isRiserEl
      ? (el.label && el.label.trim() && el.label !== 'Risers'
          ? el.label
          : riserTotalSpec((el.config as unknown) as RiserConfig))
      : el.label;
    if (isRiserEl ? riserText : (el.label && el.showLabel !== false)) {
      const lbl = document.createElementNS(svgns, 'text');
      lbl.setAttribute('x', String(stageX + el.x * STAGE_SCALE * stageScale));
      lbl.setAttribute('y', String(stageY + (el.y + el.height) * STAGE_SCALE * stageScale + 12));
      lbl.setAttribute('font-size', '9');
      lbl.setAttribute('fill', '#333333');
      lbl.textContent = riserText || el.label || '';
      svg.appendChild(lbl);
    }
  }

  return svg.outerHTML;
}

/**
 * Inline any `/stageplot-assets/*.png` referenced by an exported stage-plot
 * SVG as base64 data URIs.
 *
 * The PDF path rasterizes the SVG by loading it into an `<img>` (see
 * `svgToPngDataUrl` in RiderPage); an `<img>`-embedded SVG runs in "secure
 * static mode" and CANNOT fetch external resources, so `<image href="/stageplot-assets/...">`
 * (used by the composite DrumKit PNG assembly) would otherwise render blank
 * in the PDF. Inlining each referenced PNG as a data URI makes the SVG
 * self-contained and rasterize-safe. Each unique asset is fetched once; on
 * fetch failure the original URL is left in place (documented limitation).
 */
export async function inlineStagePlotImages(svg: string): Promise<string> {
  const refs = [...new Set(
    Array.from(svg.matchAll(/href="([^"]*stageplot-assets\/[^"]+)"/g), (m) => m[1]),
  )];
  if (refs.length === 0) return svg;
  const cache = new Map<string, string>();
  const toBase64 = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
    }
    return btoa(bin);
  };
  await Promise.all(
    refs.map(async (u) => {
      try {
        const res = await fetch(u);
        if (!res.ok) return;
        const buf = await res.arrayBuffer();
        cache.set(u, `data:image/png;base64,${toBase64(buf)}`);
      } catch {
        /* leave the original URL — image just won't render when rasterized */
      }
    }),
  );
  let out = svg;
  for (const [u, dataUri] of cache) out = out.split(`href="${u}"`).join(`href="${dataUri}"`);
  return out;
}


/** Serialise the current plot to a PNG and trigger a download. */
export function exportPng(plot: StagePlot, w: number, h: number) {
  const svgStr = stagePlotToSvg(plot, w, h);
  const svg64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plot.name || 'stage-plot'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  };
  img.src = svg64;
}
